/** @jsxImportSource @emotion/react */
import { useTheme } from '@quickbit/qb-design-system';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { PaymentIQIFrame, ErrorComponent } from 'components';
import { useMixpanel } from 'context';
import { useOnrampData } from 'onramp-pages/context';
import { useOrderStatusPolling } from 'onramp-pages/hooks/useOrderStatusPolling';
import { isBinBlock } from './utils';

export const CashierBuy = () => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { buyPreviewId, merchantId, paymentMethod } = useOnrampData();
  const { breakpoints, sizing } = useTheme();
  const { mixpanelTrackEvent } = useMixpanel({
    Product: 'TopUp',
    Page: 'Buy Cashier',
  });
  const { error } = useOrderStatusPolling({
    onStatusChange: (data, oldStatus) => {
      const { status } = data;
      if (['COMPLETE', 'CRYPTO_RECEIVED', 'CRYPTO_SOLD'].includes(status)) {
        mixpanelTrackEvent('Buy completed', {
          description: 'User completed the buy process with the cashier.',
        });
        navigate('/onramp/success');
      }
      if (status === 'CANCELLED') {
        navigate('/onramp/cancelled');
      }
      if (oldStatus === undefined) {
        // The user has refreshed the page
        if (status === 'BUY_IN_PROGRESS') {
          navigate('/onramp/processing');
        }
        if (status === 'BUY_FAILED') {
          // If the status is already 'BUY_FAILED'
          // then we should restart the whole process
          mixpanelTrackEvent('Buy failed', {
            description:
              'The buy failed and the user refreshed the page for some reason.',
          });
          navigate('/onramp');
        }
      }
    },
  });

  if (!buyPreviewId) {
    throw Error(
      `CashierBuy - Invalid state ${JSON.stringify({
        buyPreviewId,
      })}`
    );
  }

  if (error) {
    return (
      <ErrorComponent
        extraInfo={
          isBinBlock(error)
            ? formatMessage({ id: 'page.onramp.error.binBlock' })
            : formatMessage({ id: 'page.onramp.error.tryAgain' })
        }
        tryAgainAction={() => navigate('/onramp')}
      />
    );
  }

  return (
    <div
      css={{
        height: '100%',
        width: '100%',
        [breakpoints.laptopSmall]: {
          height:
            paymentMethod === 'skrill'
              ? `calc(1.9 * ${sizing.medium})`
              : sizing.large,
        },
      }}
    >
      <PaymentIQIFrame
        previewId={buyPreviewId}
        qbMerchantId={merchantId}
        product={'topup'}
        onCashierDataFailed={() => navigate(`/onramp`)}
        paymentMethod={paymentMethod}
      />
    </div>
  );
};
